  <template lang="pug">
  management-page.scrollable(title="MetricsTags"
                :creatable="false"
                :searchable="true"
                v-loading="loading && validTagUsage"
                :showHeader="false")
    template(v-slot:body)
      .chart-toolbar
        .dropdown-filters
          .dropdown-filter
            segment-select(:customer-segment-filter-options="CustomerSegmentFilterOptions"
                      :segment-alias="segmentAlias"
                      is-select-multiple
                      is-select-collapse-tags
                      @change="onSegmentFilterChange"
                      @on-segment-hover="onSegmentHover"
                      :isCollapsible="true")
          .dropdown-filter
            collapsible-select(placeholder="Filter by Tags"
                                      v-model="chosenTagFilterOptions"
                                      :value="chosenTagFilterOptions"
                                      multiple
                                      :reserve-keyword="false"
                                      @change="onTagFilterChange($event)"
                                      value-key='id'
                                      size="small")
              aiq-option(v-for="option in tagListFilterOptions"
                                  :key="option.id"
                                  :label="optionLabel(option)"
                                  :value="option")
          .dropdown-filter
            collapsible-select(placeholder="Filter by Agents of Teams"
                                         v-model="chosenAgentTeamFilterOptions"
                                          :value="chosenAgentTeamFilterOptions"
                                          multiple
                                          :reserve-keyword="false"
                                          @change="onAgentsTeamsFilterChange($event)"
                                          value-key='id'
                                          size="small")
              aiq-option(v-for="option in agentsTeamsFilterOptions"
                                      :key="option.id"
                                      :label="optionLabel(option)"
                                      :value="option")
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="12")
              overview-chart(
                          :dateRange="dateRangeUtc"
                          :filters="selectedFilters"
                          :chartConfig="tagsChartConfig"
                          :breakdown="selectedBreakdownOption.value"
                          :reportFileName="returnReportFileName('Tag Distribution')"
                          visualization="aiq-donut-chart"
                          :downloadWithTimeTicks="false"
                          :style="{ height: '100%' }"
                          )
          aiq-col(:span="12")
              overview-chart(
                          :style="{ overflow: 'visible' }"
                          :dateRange="dateRangeUtc"
                          :filters="selectedFilters"
                          :chartConfig="tagsProgressChartConfig"
                          :formatXTick="xAxisFormatForTagUsage"
                          @onXLabelsChange="xLabelsTags=$event"
                          :shouldHandlePartialDates="true"
                          :formatYTick="intCountFormatter"
                          @onChartStatus='onChartStatus($event)'
                          @onChartDataChange='onChartDataChange($event)'
                          yLabel="Count"
                          :shouldShowPartialFootnote="true"
                          :reportFileName="returnReportFileName('Tag Usage')"
                          @onChartBreakdownChange="tagUsageBucketOption=$event"
                          :getOriginalData="fetchAllTagsForExport"
                          deriveLinesFromResponse)
      .tags-table-toolbar
        .flex-spacer
        aiq-button.export(@click="exportTable")
          i.iq-ico-export
      aiq-col(:span="24")
        tags-table(:dateRange="dateRangeUtc"
                   ref="TagsTable"
                   :filters="selectedFilters"
                   :namedFilters="namedFilters"
                  )
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from '@/localPlugins/dayjs';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import SegmentSelect from '@/components/SegmentsSelect/SegmentsSelect.vue';
import CollapsibleSelect from '@/components/CollapsibleSelect/CollapsibleSelect.vue';
import TagsTable from './TagsTable.vue';
import tagsChartConfig from '../../../configs/tagsDistribution';
import tagsProgressChartConfig from '../../../configs/tagsProgress';
import {
  datePickerDefaultOptions,
  selectedBreakdownOption,
  defaultDateRange,
  filterBreakdownOptions,
  dstDateRangeUtc,
  lastAvailableMonthAndQuarterRange,
  createPickerDateRangeOption,
} from '../../../configs/dateRange';
import { intTickFormatter } from '../../../helpers/formatters';
import { loadFilterOptions } from '../../../helpers/filterHelper';
import PlatformFiltersMixin from '../../../Platform/children/selectableFilters/SelectableFilters';

export default {
  pageResource: '/metrics/insights',
  components: {
    TagsTable,
    ManagementPage,
    OverviewChart,
    SegmentSelect,
    CollapsibleSelect,
  },
  computed: {
    ...mapGetters({
      CustomerSegmentFilterOptions: 'configs/segments',
    }),
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    namedFilters() {
      return {
        customer_segments: this.chosenCustomerSegmentFilterOptions.map(x => x.name),
        teams: this.chosenAgentTeamFilterOptions.map(x => x.name),
      };
    },
    // TODO: Create a mixin that has this computed value,
    // along with other common properties and functions in metrics
    filteredBreakdownOptions() {
      return filterBreakdownOptions(this.dateRange);
    },
  },
  watch: {
    dateRange: debounce(async function onDateRangeChange(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loading = true;
        await loadFilterOptions(this, this.customerSegmentsEndpoint);
        await loadFilterOptions(this, this.tagsEndpoint);
      }
    }, 1),
    filteredBreakdownOptions(newOptions) {
      if (!newOptions.find(o => o.value === this.selectedBreakdownOption.value)) {
        this.selectedBreakdownOption = newOptions.find(o => o.isDefault);
      }
    },
  },
  mixins: [PlatformFiltersMixin],
  data() {
    return {
      query: null,
      xLabelsTags: {},
      selectedBreakdownOption: selectedBreakdownOption(),
      durationFormatter: this.$aiq.formatters.timeDelta,
      dateRange: defaultDateRange(),
      tagsChartConfig,
      tagsProgressChartConfig,
      intCountFormatter: intTickFormatter,
      customerSegmentsEndpoint: 'metrics_by_customer_segment',
      tagsEndpoint: 'tags_list',
      teamsEndpoint: 'all_teams',
      tagListFilterOptions: [],
      selectedFilters: [],
      chosenCustomerSegmentFilterOptions: [],
      chosenTagFilterOptions: [],
      chosenAgentTeamFilterOptions: [],
      loading: false,
      validTagUsage: false,
      segmentAlias: '',
    };
  },
  methods: {
    fetchAllTagsForExport() {
      // Fetch ALL tags (not limited to top 10) for export
      const [period_start, period_end] = this.dateRangeUtc;
      return this.$store.dispatch('metrics/getMetric', ['tags_progress', {
        period_start,
        period_end,
        bucket: this.tagUsageBucketOption?.value || 'day',
        filters: this.selectedFilters,
        limit_results: false,
      }]);
    },
    exportTable() {
      this.$refs.TagsTable.exportTable();
    },
    onChartStatus(action) {
      if (action === 'onrefresh') {
        this.loading = true;
      } else if (action === 'onrendered') {
        this.loading = false;
      }
    },
    onChartDataChange(data) {
      this.validTagUsage = !isEmpty(data.dataset);
      const dataPoints = data?.dataset?.[0]?.data_points || [];
      this.tagUsageChartData = dataPoints;
    },
    onSegmentHover(segment) {
      this.segmentAlias = get(segment, 'alias', '');
    },
    onSegmentFilterChange(segments) {
      this.chosenCustomerSegmentFilterOptions = segments;
      this.onCustomerSegmentFilterChange(segments);
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    isDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      const isPartial = this.tagUsageChartData.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
    xAxisFormatForTagUsage(date) {
      return `${this.xLabelsTags[dayjs(date).valueOf()]} ${this.isDataPartial(date) ? ' *' : ''}`;
    },
  },
  async mounted() {
    this.$store.dispatch('configs/getCustomerSegments');
    await loadFilterOptions(this, this.tagsEndpoint);
    await loadFilterOptions(this, this.teamsEndpoint);
  },
};
</script>

<style lang="scss" scoped>
.scrollable {
  overflow-y: auto;
}

.tags-table-toolbar {
    display: flex;
    margin-bottom: 2px;
    .flex-spacer {
    flex-grow: 1;
  }
}
.chart-grid {
  margin-bottom: 12px;
  .el-col {
    margin-bottom: 16px;
  }
}

.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.chart-toolbar {
  display: flex;
  margin-bottom: 20px;
  .dropdown-filters {
    display: flex;
    align-items: flex-start;
    width: 60%;

    .dropdown-filter {
      margin-right: 12px;
      width: 25em;
      :deep(.el-select) {
        width: 75%;
      }
    }
  }
  .flex-spacer {
    flex-grow: 1;
    .el-button-group .el-button {
      padding: 9px 9px;
    }
  }

   .search {
    max-width: 246px;
  }

  .time-range-filter {
    position: relative;

    margin-left: auto;
    .el-date-editor.el-input__inner {
      height: 32px;
    }
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }

  .el-button.export {
      height: 30px;
      width: 40px;
      padding: 0;
      margin-left: 16px;
  }
}

::v-deep .chart-container svg{
  overflow: visible !important;
}
</style>
